import { useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, X, FileText, Loader2, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { SingleImageField, uploadFile } from "@/components/ImageUploader";

const inputCls = "w-full border border-forest/15 bg-white px-3.5 py-2.5 text-sm text-forest focus:border-gold focus:outline-none";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.2em] text-forest/55 mb-1.5";
const btnCls = "bg-forest px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-parchment hover:bg-forest-leaf transition-colors disabled:opacity-60";

export function FounderSection({ settings, setS, saveSettings, saving }) {
  const f = settings.founder || {};
  return (
    <div data-testid="admin-founder">
      <h3 className="font-serif text-2xl text-forest">About the Founder</h3>
      <p className="mt-1 text-xs text-forest/50">Shown on the About page.</p>
      <div className="mt-4 grid gap-4 border border-forest/10 bg-white p-4 sm:grid-cols-[1fr_220px]">
        <div className="space-y-3">
          <div><label className={labelCls}>Name</label><input className={inputCls} data-testid="admin-founder-name" value={f.name || ""} onChange={(e) => setS("founder.name", e.target.value)} /></div>
          <div><label className={labelCls}>Title</label><input className={inputCls} data-testid="admin-founder-title" value={f.title || ""} onChange={(e) => setS("founder.title", e.target.value)} /></div>
          <div><label className={labelCls}>Company Intro</label><textarea rows={3} className={inputCls} data-testid="admin-founder-intro" value={f.intro || ""} onChange={(e) => setS("founder.intro", e.target.value)} /></div>
          <div><label className={labelCls}>Founder Bio</label><textarea rows={3} className={inputCls} data-testid="admin-founder-bio" value={f.bio || ""} onChange={(e) => setS("founder.bio", e.target.value)} /></div>
        </div>
        <SingleImageField label="Founder Photo" testId="admin-founder-photo" value={f.photo || ""} onChange={(v) => setS("founder.photo", v)} />
      </div>
      <button onClick={() => saveSettings(["founder"])} disabled={saving} data-testid="admin-save-founder" className={`mt-4 ${btnCls}`}>Save Founder</button>
    </div>
  );
}

export function CategoryBannersSection({ settings, setS, saveSettings, saving }) {
  return (
    <div data-testid="admin-category-banners">
      <h3 className="font-serif text-2xl text-forest">Category Banners</h3>
      <p className="mt-1 text-xs text-forest/50">Shown on the homepage category grid and as hero backgrounds on the Rice, Spices and Tea pages.</p>
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        {["rice", "spices", "tea"].map((cat) => (
          <div key={cat} className="border border-forest/10 bg-white p-4 space-y-3" data-testid={`admin-category-${cat}`}>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-dark">{cat}</p>
            <div><label className={labelCls}>Title</label><input className={inputCls} data-testid={`admin-category-${cat}-title`} value={settings.categories[cat]?.title || ""} onChange={(e) => setS(`categories.${cat}.title`, e.target.value)} /></div>
            <div><label className={labelCls}>Description</label><textarea rows={2} className={inputCls} value={settings.categories[cat]?.description || ""} onChange={(e) => setS(`categories.${cat}.description`, e.target.value)} /></div>
            <SingleImageField label="Banner Photo" testId={`admin-category-${cat}-image`} value={settings.categories[cat]?.image || ""} onChange={(v) => setS(`categories.${cat}.image`, v)} />
          </div>
        ))}
      </div>
      <button onClick={() => saveSettings(["categories"])} disabled={saving} data-testid="admin-save-categories" className={`mt-4 ${btnCls}`}>Save Category Banners</button>
    </div>
  );
}

export function CertificationsSection({ settings, setS, saveSettings, saving }) {
  const certs = settings.certifications || [];
  return (
    <div data-testid="admin-certifications">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-serif text-2xl text-forest">Certifications</h3>
          <p className="mt-1 text-xs text-forest/50">Displayed on the Quality page. Add only confirmed certifications.</p>
        </div>
        <button onClick={() => setS("certifications", [...certs, { name: "", logo: "", description: "" }])} data-testid="admin-add-certification" className="inline-flex items-center gap-1.5 border border-forest/20 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest hover:border-gold transition-colors"><Plus className="h-3.5 w-3.5" /> Add</button>
      </div>
      <div className="mt-4 space-y-4">
        {certs.length === 0 && <p className="border border-dashed border-forest/20 bg-white p-6 text-center text-sm text-forest/50" data-testid="admin-certifications-empty">No certifications yet. Click Add to create one.</p>}
        {certs.map((c, i) => (
          <div key={i} className="relative grid gap-4 border border-forest/10 bg-white p-4 sm:grid-cols-[1fr_220px]" data-testid={`admin-certification-${i}`}>
            <button onClick={() => setS("certifications", certs.filter((_, j) => j !== i))} data-testid={`admin-remove-certification-${i}`} className="absolute right-3 top-3 text-forest/40 hover:text-terracotta transition-colors" aria-label="Remove certification"><X className="h-4 w-4" /></button>
            <div className="space-y-3">
              <div><label className={labelCls}>Name</label><input className={inputCls} data-testid={`admin-certification-name-${i}`} value={c.name} onChange={(e) => setS(`certifications.${i}.name`, e.target.value)} placeholder="e.g. FSSAI, APEDA, ISO 22000" /></div>
              <div><label className={labelCls}>Short Description</label><textarea rows={2} className={inputCls} data-testid={`admin-certification-desc-${i}`} value={c.description} onChange={(e) => setS(`certifications.${i}.description`, e.target.value)} placeholder="What this certification covers" /></div>
            </div>
            <SingleImageField label="Logo" testId={`admin-certification-logo-${i}`} value={c.logo} onChange={(v) => setS(`certifications.${i}.logo`, v)} />
          </div>
        ))}
      </div>
      <button onClick={() => saveSettings(["certifications"])} disabled={saving} data-testid="admin-save-certifications" className={`mt-4 ${btnCls}`}>Save Certifications</button>
    </div>
  );
}

export function BrochureSection({ settings, setS, saveSettings, saving }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const b = settings.brochure || {};

  const onFile = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Please choose a PDF file."); return; }
    setUploading(true);
    try {
      const data = await uploadFile(file);
      setS("brochure", { ...b, url: data.url, filename: data.filename, size: data.size, enabled: true });
      toast.success("Brochure uploaded. Click Save to publish.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="admin-brochure">
      <h3 className="font-serif text-2xl text-forest">Product Brochure (PDF)</h3>
      <p className="mt-1 text-xs text-forest/50">Buyers can download this from the Products page and footer.</p>
      <div className="mt-4 border border-forest/10 bg-white p-4 space-y-4">
        <label className="flex items-center gap-3 text-sm text-forest cursor-pointer">
          <input type="checkbox" checked={!!b.enabled} onChange={(e) => setS("brochure.enabled", e.target.checked)} data-testid="admin-brochure-enabled" className="h-4 w-4 accent-[#C69A43]" />
          Show the brochure download on the website
        </label>
        <div><label className={labelCls}>Button Title</label><input className={inputCls} data-testid="admin-brochure-title" value={b.title || ""} onChange={(e) => setS("brochure.title", e.target.value)} placeholder="Product Catalogue" /></div>
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
          data-testid="admin-brochure-dropzone"
          className="flex cursor-pointer items-center gap-4 border border-dashed border-forest/30 px-4 py-5 hover:border-gold transition-colors"
        >
          {uploading ? <Loader2 className="h-6 w-6 animate-spin text-gold-dark" /> : <FileText className="h-6 w-6 text-gold-dark" />}
          <div className="min-w-0">
            {b.url ? (
              <>
                <p className="truncate text-sm text-forest" data-testid="admin-brochure-filename">{b.filename || "brochure.pdf"} · {Math.round((b.size || 0) / 1024)} KB</p>
                <p className="text-xs text-forest/50">Click or drop a new PDF to replace it.</p>
              </>
            ) : (
              <>
                <p className="text-sm text-forest">{uploading ? "Uploading..." : "Click or drop your PDF catalogue here"}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-forest/45">PDF · max 25 MB</p>
              </>
            )}
          </div>
          <input ref={inputRef} type="file" accept="application/pdf" className="hidden" data-testid="admin-brochure-file-input" onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
        {b.url && (
          <div className="flex flex-wrap gap-3">
            <a href={`${process.env.REACT_APP_BACKEND_URL}${b.url}`} target="_blank" rel="noopener noreferrer" data-testid="admin-brochure-preview" className="text-xs font-semibold uppercase tracking-[0.14em] text-gold-dark hover:underline">Preview PDF</a>
            <button onClick={() => setS("brochure", { ...b, url: "", filename: "", size: 0, enabled: false })} data-testid="admin-brochure-remove" className="text-xs font-semibold uppercase tracking-[0.14em] text-terracotta hover:underline">Remove</button>
          </div>
        )}
      </div>
      <button onClick={() => saveSettings(["brochure"])} disabled={saving} data-testid="admin-save-brochure" className={`mt-4 ${btnCls}`}>Save Brochure</button>
    </div>
  );
}

export function AlertsSection({ settings, setS, saveSettings, saving }) {
  const [testing, setTesting] = useState(false);
  const a = settings.alerts || {};

  const sendTest = async () => {
    setTesting(true);
    try {
      await saveSettings(["alerts"]);
      await api.post("/admin/alerts/test-whatsapp");
      toast.success("Test message sent. Check your WhatsApp.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="border border-forest/10 bg-white p-5" data-testid="admin-alerts-section">
      <h3 className="font-serif text-xl text-forest">WhatsApp Alerts</h3>
      <p className="mt-0.5 text-xs text-forest/55">Every new quote request and contact message is sent instantly to this WhatsApp number via Twilio.</p>
      <div className="mt-4 grid sm:grid-cols-[auto_1fr] gap-4 items-end">
        <label className="flex items-center gap-2.5 pb-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-forest cursor-pointer">
          <input type="checkbox" checked={a.whatsapp_enabled !== false} onChange={(e) => setS("alerts.whatsapp_enabled", e.target.checked)} data-testid="admin-alerts-enabled" className="h-4 w-4 accent-[#C69A43]" />
          Enabled
        </label>
        <div><label className={labelCls}>Alert Number (international format, digits only)</label><input className={inputCls} data-testid="admin-alerts-number" value={a.whatsapp_number || ""} onChange={(e) => setS("alerts.whatsapp_number", e.target.value.replace(/[^0-9]/g, ""))} placeholder="919849966082" /></div>
      </div>
      <p className="mt-3 text-[11px] leading-relaxed text-forest/50">Using the Twilio Sandbox? The alert number must first send <span className="font-mono text-forest">join &lt;your-sandbox-code&gt;</span> to <span className="font-mono text-forest">+1 415 523 8886</span> on WhatsApp (once every 72 hours). Alerts arrive as free-form messages while a 24-hour session is open; outside it, Twilio requires an approved template.</p>
      <div className="mt-4"><label className={labelCls}>Approved Template SID (optional, starts with HX — template must contain a single {"{{1}}"} placeholder)</label><input className={inputCls} data-testid="admin-alerts-template" value={a.template_sid || ""} onChange={(e) => setS("alerts.template_sid", e.target.value.trim())} placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" /></div>
      <div className="mt-4 flex flex-wrap gap-3">
        <button onClick={() => saveSettings(["alerts"])} disabled={saving} data-testid="admin-save-alerts" className={btnCls}>Save Alerts</button>
        <button onClick={sendTest} disabled={testing || saving} data-testid="admin-test-whatsapp" className="inline-flex items-center gap-2 border border-forest/20 px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-forest hover:border-gold transition-colors disabled:opacity-60">
          {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send Test Message
        </button>
      </div>
    </div>
  );
}
